/**
 * Utility functions for cache management
 */

/**
 * Creates a hash string from group IDs and query parameters
 * @param groupIds - Array of group IDs
 * @param queryParams - Object containing query parameters
 * @returns A string hash that can be used as a cache ID
 */
export const createCacheId = (
  groupIds: string[],
  queryParams: Record<string, any> | string,
  prefix = ""
): string => {
  let queryStringRecord: Record<string, string> = {};
  if (typeof queryParams === "string") {
    queryParams.split("&").forEach((param) => {
      const [key, value] = param.split("=");
      queryStringRecord[key] = value;
    });
  } else {
    queryStringRecord = queryParams;
  }
  // Sort group IDs to ensure consistent order
  const sortedGroupIds = [...groupIds].sort();

  // Create a string representation of the query parameters
  const queryString = Object.entries(queryStringRecord)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB)) // Sort by keys for consistency
    .map(([key, value]) => {
      // Handle arrays (like cp parameters)
      if (Array.isArray(value)) {
        return `${key}=${value.sort().join(",")}`;
      }
      return `${key}=${value}`;
    })
    .join("&");

  // Combine group IDs and query string
  const combinedString = `${prefix}:${sortedGroupIds.join(",")}:${queryString}`;

  // Create a simple hash function
  let hash = 0;
  for (let i = 0; i < combinedString.length; i++) {
    const char = combinedString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to a positive hex string
  return Math.abs(hash).toString(16);
};
